import React, { useState, useEffect, useCallback } from 'react';
import { Container, Tab, Tabs, Spinner, Alert, Navbar, Nav } from 'react-bootstrap';
import { ProjectManager } from './components/ProjectManager';
import { GanttChart } from './components/GanttChart';
import { GoalDetail } from './components/GoalDetail';
import { Project, Goal, Task, TimeEntry, AuthUser } from './types/models';
import { api } from './services/api';
import { consumeTokenFromURL, getToken, clearToken, redirectToLogin } from './auth';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

function App() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [activeTab, setActiveTab] = useState<string>('projects');
  const [selectedGoal, setSelectedGoal] = useState<{ projectId: string; goalId: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadProjects = useCallback(async () => {
    try {
      setError(null);
      const data = await api.getProjects();
      setProjects(data);
    } catch (err: any) {
      if (err?.message !== 'Unauthorized') {
        setError('Failed to load projects. Please try again.');
      }
    }
  }, []);

  useEffect(() => {
    // Pull token from redirect URL if present
    consumeTokenFromURL();

    if (!getToken()) {
      redirectToLogin();
      return;
    }

    Promise.all([
      api.getMe().then(setCurrentUser).catch(() => {}),
      loadProjects(),
    ]).finally(() => setLoading(false));
  }, [loadProjects]);

  const handleSignOut = () => {
    clearToken();
    redirectToLogin();
  };

  const handleProjectCreate = async (projectData: Omit<Project, 'id' | 'createdAt' | 'updatedAt' | 'goals'>) => {
    try {
      const newProject = await api.createProject(projectData);
      setProjects(prev => [...prev, newProject]);
    } catch {
      setError('Failed to create project.');
    }
  };

  const handleGoalCreate = async (projectId: string, goalData: Omit<Goal, 'id' | 'tasks'>) => {
    try {
      const newGoal = await api.createGoal(projectId, goalData);
      setProjects(prev =>
        prev.map(project => {
          if (project.id !== projectId) return project;
          return { ...project, goals: [...project.goals, newGoal], updatedAt: new Date() };
        })
      );
    } catch {
      setError('Failed to create goal.');
    }
  };

  const handleTaskCreate = async (goalId: string, taskData: Omit<Task, 'id'>, projectId: string) => {
    try {
      const newTask = await api.createTask(projectId, goalId, taskData);
      setProjects(prev =>
        prev.map(project => ({
          ...project,
          goals: project.goals.map(goal => {
            if (goal.id !== goalId) return goal;
            return { ...goal, tasks: [...goal.tasks, newTask] };
          }),
          updatedAt: new Date(),
        }))
      );
    } catch {
      setError('Failed to create task.');
    }
  };

  const handleTaskMove = (taskId: string, newGoalId: string, newStatus: Task['status'], newOrder: number) => {
    setProjects(
      projects.map((project) => {
        let movedTask: Task | null = null;
        const updatedGoals = project.goals.map((goal) => ({
          ...goal,
          tasks: goal.tasks.filter((task) => {
            if (task.id === taskId) {
              movedTask = { ...task, goalId: newGoalId, status: newStatus, order: newOrder };
              return false;
            }
            return true;
          }),
        }));
        if (!movedTask) return project;
        return {
          ...project,
          goals: updatedGoals.map((goal) => {
            if (goal.id !== newGoalId) return goal;
            const tasks = [...goal.tasks];
            tasks.splice(newOrder, 0, movedTask!);
            return { ...goal, tasks };
          }),
          updatedAt: new Date(),
        };
      })
    );
  };

  const handleAddTimeEntry = (taskId: string, entry: Omit<TimeEntry, 'id'>) => {
    const newEntry: TimeEntry = { ...entry, id: Date.now().toString() };
    setProjects(
      projects.map((project) => ({
        ...project,
        goals: project.goals.map((goal) => ({
          ...goal,
          tasks: goal.tasks.map((task) => {
            if (task.id !== taskId) return task;
            return { ...task, timeEntries: [...(task.timeEntries ?? []), newEntry] };
          }),
        })),
      }))
    );
  };

  const handleDeleteTimeEntry = (taskId: string, entryId: string) => {
    setProjects(
      projects.map((project) => ({
        ...project,
        goals: project.goals.map((goal) => ({
          ...goal,
          tasks: goal.tasks.map((task) => {
            if (task.id !== taskId) return task;
            return { ...task, timeEntries: (task.timeEntries ?? []).filter(e => e.id !== entryId) };
          }),
        })),
      }))
    );
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="App">
      <Navbar bg="dark" variant="dark" className="px-3 mb-3">
        <Navbar.Brand>Project Tracker</Navbar.Brand>
        <Nav className="ms-auto align-items-center gap-3">
          {currentUser && (
            <div className="d-flex align-items-center gap-2">
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: '#0d6efd',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 600,
                  fontSize: 14,
                  flexShrink: 0,
                }}
              >
                {currentUser.name ? currentUser.name.charAt(0).toUpperCase() : currentUser.email.charAt(0).toUpperCase()}
              </div>
              <span className="text-white-50 small">{currentUser.name || currentUser.email}</span>
            </div>
          )}
          <Nav.Link onClick={handleSignOut} className="text-white-50">Sign Out</Nav.Link>
        </Nav>
      </Navbar>
      <Container fluid>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {selectedGoal ? (() => {
          const proj = projects.find(p => p.id === selectedGoal.projectId);
          const goal = proj?.goals.find(g => g.id === selectedGoal.goalId);
          if (!proj || !goal) return null;
          return (
            <GoalDetail
              project={proj}
              goal={goal}
              onBack={() => setSelectedGoal(null)}
              onTaskMove={handleTaskMove}
              onTaskCreate={handleTaskCreate}
              onAddTimeEntry={handleAddTimeEntry}
              onDeleteTimeEntry={handleDeleteTimeEntry}
            />
          );
        })() : (
          <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'projects')} className="mb-3">
            <Tab eventKey="projects" title="Projects">
              <ProjectManager
                projects={projects}
                onProjectCreate={handleProjectCreate}
                onGoalCreate={handleGoalCreate}
                onTaskCreate={handleTaskCreate}
                onProjectSelect={() => setActiveTab('gantt')}
                onGoalSelect={(projectId, goalId) => setSelectedGoal({ projectId, goalId })}
              />
            </Tab>
            <Tab eventKey="gantt" title="Gantt Chart">
              <GanttChart
                projects={projects}
                onTaskMove={handleTaskMove}
                onAddTimeEntry={handleAddTimeEntry}
                onDeleteTimeEntry={handleDeleteTimeEntry}
              />
            </Tab>
          </Tabs>
        )}
      </Container>
    </div>
  );
}

export default App;
